import React, { useState, createContext, useEffect } from "react";
import { StateNavigator, HTML5HistoryManager } from "navigation";
import { NavigationHandler, SceneView } from "navigation-react";
import LoaderAnimation from "./LoaderAnimation";
import Login from "./Login";
import SalesOrder from "./SalesOrder";
import OrderRegister from "./OrderRegister";
import ViewSalesInvoiceDetails from "./ViewSalesInvoiceDetails";
import TableManager from "./TableManager";
import ReceiveStock from "./ReceiveStock";
import CashRegister from "./CashRegister";
import EditSalesOrder from "./EditSalesOrder";
import MenuRegister from "./MenuRegister";
import AddCTScanRequestRegister from "./AddCTScanRequestRegister";
import StaffSignRegister from "./StaffSignRegister";
import DashboardContent from "./DashboardContent";
import DeliveriesRegister from "./DeliveriesRegister";
import VisitorSignRegisterDetails from "./VisitorSignRegisterDetails";
import VisitorEditSignRegister from "./VisitorEditSignRegister";
import DeliveriesEditRegister from "./DeliveriesEditRegister";
import StaffRegister from "./StaffRegister";
import StaffEditRegister from "./StaffEditRegister";
import StaffAddRegister from "./StaffAddRegister";
import OnlineOrders from "./OnlineOrders";
import SupplierRegister from "./SupplierRegister";
import CategoryRegister from "./CategoryRegister";
import { VisitorDetailsProvider } from "./VisitorDetailsContext";
import Inventory from "./Inventory";
import ReceivedStockRegister from "./ReceivedStockRegister";
import TransferStockRegister from "./TransferStockRegister";
import SecureLS from "secure-ls";
import "./styles.css";
import ItemizedSalesRegister from "./ItemizedSalesRegister";
import DailyReport from "./DailyReport";
import CoffeeShopMenu from "./CoffeeShopMenu";
import VoidRegister from "./VoidRegister";

const ls = new SecureLS({ encodingType: "aes" });

// ✅ Role Context with setter
export const RoleContext = createContext({ roles: [], setRoles: () => {} });

// Navigation setup
const stateNavigator = new StateNavigator(
  [
    { key: "login", route: "login" },
    { key: "salesorder", route: "sales-order" },
    { key: "orderregister", route: "order-register" },
    { key: "viewsalesinvoicedetails", route: "view-sales-invoice-details" },
    { key: "tablemanager", route: "table-manager" },
    { key: "receivestock", route: "receive-stock" },
    { key: "cashregister", route: "cash-register" },
    { key: "editsalesorder", route: "edit-sales-order" },
    { key: "itemizedsalesregister", route: "itemized-sales-register" },
    { key: "menuregister", route: "menu-register" },
    { key: "addctscanrequestregister", route: "add-ct-scan-request-register" },
    { key: "staffsignregister", route: "staff-sign-register" },
    { key: "dashboardcontent", route: "dashboard-content" },
    { key: "deliveriesregister", route: "deliveries-register" },
    { key: "deliverieseditregister", route: "deliveries-edit-register" },
    { key: "staffregister", route: "staff-register" },
    { key: "staffeditregister", route: "staff-edit-register" },
    { key: "staffaddregister", route: "staff-add-register" },
    { key: "onlineorders", route: "online-orders" },
    { key: "inventory", route: "inventory" },
    { key: "receivedstockregister", route: "received-stock-register" },
    { key: "transferstockregister", route: "transfer-stock-register" },
    {
      key: "visitorsignregisterdetails",
      route: "visitor-signregister-details",
    },
    { key: "visitoreditsignregister", route: "visitor-editsign-register" },
    { key: "supplierregister", route: "supplier-register" },
    { key: "categoryregister", route: "category-register" },
    { key: "dailyreport", route: "daily-report" },
    { key: "coffeeshopmenu", route: "menu" },
    { key: "voidregister", route: "void-register" },
  ],
  new HTML5HistoryManager(),
);

// Automatically navigate root `/` to `/login`
if (!window.location.pathname || window.location.pathname === "/") {
  stateNavigator.navigate("login", { replace: true });
}

stateNavigator.start();

const App = () => {
  const [roles, setRoles] = useState(ls.get("currentuserrole") || []);

  const username = ls.get("username");

  // ✅ Check on app load
  useEffect(() => {
    // If not logged in and not on login page, go to login
    if (!username) {
      ls.clear();
      stateNavigator.navigate("login", { replace: true });
    }
  }, [username]);

  return (
    <RoleContext.Provider value={{ roles, setRoles }}>
      <VisitorDetailsProvider>
        <NavigationHandler stateNavigator={stateNavigator}>
          <SceneView active="login">
            <Login />
          </SceneView>

          <SceneView active="orderregister">
            <OrderRegister />
          </SceneView>

          <SceneView active="salesorder">
            <SalesOrder />
          </SceneView>

          <SceneView active="viewsalesinvoicedetails">
            <ViewSalesInvoiceDetails />
          </SceneView>

          <SceneView active="tablemanager">
            <TableManager />
          </SceneView>

          <SceneView active="receivestock">
            <ReceiveStock />
          </SceneView>

          <SceneView active="cashregister">
            <CashRegister />
          </SceneView>

          <SceneView active="editsalesorder">
            <EditSalesOrder />
          </SceneView>

          <SceneView active="itemizedsalesregister">
            <ItemizedSalesRegister />
          </SceneView>

          <SceneView active="menuregister">
            <MenuRegister />
          </SceneView>

          <SceneView active="addctscanrequestregister">
            <AddCTScanRequestRegister />
          </SceneView>

          <SceneView active="dashboardcontent">
            <DashboardContent />
          </SceneView>

          <SceneView active="staffsignregister">
            <StaffSignRegister />
          </SceneView>

          <SceneView active="deliveriesregister">
            <DeliveriesRegister />
          </SceneView>

          <SceneView active="visitorsignregisterdetails">
            <VisitorSignRegisterDetails />
          </SceneView>

          <SceneView active="visitoreditsignregister">
            <VisitorEditSignRegister />
          </SceneView>

          <SceneView active="deliverieseditregister">
            <DeliveriesEditRegister />
          </SceneView>

          <SceneView active="staffregister">
            <StaffRegister />
          </SceneView>

          <SceneView active="staffeditregister">
            <StaffEditRegister />
          </SceneView>

          <SceneView active="staffaddregister">
            <StaffAddRegister />
          </SceneView>
          <SceneView active="onlineorders">
            <OnlineOrders />
          </SceneView>

          <SceneView active="receivedstockregister">
            <ReceivedStockRegister />
          </SceneView>

          <SceneView active="transferstockregister">
            <TransferStockRegister />
          </SceneView>

          <SceneView active="categoryregister">
            <CategoryRegister />
          </SceneView>

          <SceneView active="supplierregister">
            <SupplierRegister />
          </SceneView>

          <SceneView active="inventory">
            <Inventory />
          </SceneView>

          <SceneView active="dailyreport">
            <DailyReport />
          </SceneView>

          <SceneView active="coffeeshopmenu">
            <CoffeeShopMenu />
          </SceneView>

          <SceneView active="voidregister">
            <VoidRegister />
          </SceneView>

          <SceneView active="root">
            <LoaderAnimation loadingTxt="Initializing..." />
          </SceneView>
        </NavigationHandler>
      </VisitorDetailsProvider>
    </RoleContext.Provider>
  );
};

export default App;
